/* === AgPagination — Anagogia Design System === */

import styles from './AgPagination.module.css';

export interface AgPaginationProps {
  /** Total number of pages */
  count: number;
  /** Current page (1-indexed) */
  page: number;
  /** Called when page changes */
  onChange: (page: number) => void;
  /** Show first/last buttons */
  showFirstButton?: boolean;
  /** Show last/last buttons */
  showLastButton?: boolean;
  /** Color */
  color?: 'primary' | 'secondary';
  /** Disabled state */
  disabled?: boolean;
  /** Additional class */
  className?: string;
}

/**
 * Pagination numérotée avec navigation précédent/suivant.
 * @category Affichage
 */
function AgPagination({
  count,
  page,
  onChange,
  showFirstButton = false,
  showLastButton = false,
  color = 'primary',
  disabled = false,
  className,
}: AgPaginationProps) {
  if (count <= 1) return null;

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    const delta = 1; // pages around current

    // Always show first page
    pages.push(1);

    const rangeStart = Math.max(2, page - delta);
    const rangeEnd = Math.min(count - 1, page + delta);

    if (rangeStart > 2) pages.push('ellipsis');

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < count - 1) pages.push('ellipsis');

    // Always show last page
    if (count > 1) pages.push(count);

    return pages;
  };

  const handleClick = (newPage: number) => {
    if (disabled || newPage < 1 || newPage > count || newPage === page) return;
    onChange(newPage);
  };

  const cls = [styles.root, className].filter(Boolean).join(' ');

  return (
    <nav className={cls} aria-label="pagination">
      {showFirstButton && (
        <button
          className={styles.navButton}
          onClick={() => handleClick(1)}
          disabled={disabled || page === 1}
          aria-label="Première page"
        >
          ⟨⟨
        </button>
      )}

      <button
        className={styles.navButton}
        onClick={() => handleClick(page - 1)}
        disabled={disabled || page === 1}
        aria-label="Page précédente"
      >
        ⟨
      </button>

      {getPageNumbers().map((item, index) => {
        if (item === 'ellipsis') {
          return (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>
              …
            </span>
          );
        }

        const isActive = item === page;
        const btnCls = [
          styles.pageButton,
          isActive ? styles.active : undefined,
          isActive && color === 'primary' ? styles.colorPrimary : undefined,
          isActive && color === 'secondary' ? styles.colorSecondary : undefined,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={item}
            className={btnCls}
            onClick={() => handleClick(item)}
            disabled={disabled}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`Page ${item}`}
          >
            {item}
          </button>
        );
      })}

      <button
        className={styles.navButton}
        onClick={() => handleClick(page + 1)}
        disabled={disabled || page === count}
        aria-label="Page suivante"
      >
        ⟩
      </button>

      {showLastButton && (
        <button
          className={styles.navButton}
          onClick={() => handleClick(count)}
          disabled={disabled || page === count}
          aria-label="Dernière page"
        >
          ⟩⟩
        </button>
      )}
    </nav>
  );
}

export { AgPagination };
export default AgPagination;
